import { TEST_IDS } from '@va/constants';
import { getProviderName, getSupportCenterDomain } from '@va/dashboard/selectors/customization';
import { PageHeaderTooltipOption } from '@va/dashboard/ui/components';
import { CheckmarkIcon, CloseIconWithCircle, CopyIcon, CustomerSupport } from '@va/icons';
import { useTranslate } from '@va/localization';
import { PrivacyLevelsEnum } from '@va/standalone/shared/types';
import { Button, Paragraph, fontWeights, paragraphSizes } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { isWixMiniApp } from '@va/util/helpers';
import { useCopyToClipboard } from '@va/util/hooks';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PrivacyLevelFeature } from './PrivacyLevelFeatureCard';

const styles = {
  default: {
    checkMarkColor: '#06A25A',
    titleClassName: undefined,
  },
  'wix-mini': {
    checkMarkColor: 'var(--color-primary)',
    titleClassName: 'text-text-primary font-medium!',
  },
};

const usePrivacyLevelFeatures = (variant: 'default' | 'wix-mini' = 'default') => {
  const translate = useTranslate();

  const labelsTranslations = useMemo(() => {
    return {
      usesCookies: translate('all.privacyCenter.privacyLevels.label.usesCookies'),
      regulationName: translate('all.privacyCenter.privacyLevels.label.regulationName'),
      identifyingTechnology: translate('all.privacyCenter.privacyLevels.label.identifyingTechnology'),
      privacyPolicyDisclaimer: translate('all.privacyCenter.privacyLevels.label.whatToAddToPrivacyPolicy'),
      consentNeededForVA: translate('all.privacyCenter.privacyLevels.label.consentNeededForVA'),
      ipAddressTracking: translate('all.privacyCenter.privacyLevels.label.ipAddressTracking'),
      individualPageHistory: translate('all.privacyCenter.privacyLevels.label.individualPageHistory'),
      returningVisitorTracking: translate('all.privacyCenter.privacyLevels.label.returningVisitorTracking'),
      screenResolutionTracking: translate('all.privacyCenter.privacyLevels.label.screenResolutionTracking'),
      visitorLocationTracking: translate('all.privacyCenter.privacyLevels.label.visitorLocationTracking'),
    };
  }, [translate]);

  const checkMarkIcon = useMemo(() => <CheckmarkIcon color={styles[variant].checkMarkColor} />, [variant]);
  const closeIconDevilGray = useMemo(() => <CloseIconWithCircle color='#696969' />, []);

  const defaultPrivacy = useMemo<PrivacyLevelFeature[]>(() => {
    return [
      {
        title: labelsTranslations.usesCookies,
        subTitle: translate('label.noCookieUse'),
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.regulationName,
        subTitle: translate('all.privacyCenter.privacyLevels.label.outSideEurope'),
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.privacyPolicyDisclaimer,
        titleTooltip: <DisclaimerTooltip />,
        subTitle: <DisclaimerText className='mt-3' />,
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.identifyingTechnology,
        subTitle: translate('all.privacyCenter.privacyLevels.label.fingerprint'),
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.consentNeededForVA,
        subTitle: translate('all.privacyCenter.privacyLevels.label.consentDependingOnLocation'),
        titleClassName: styles[variant].titleClassName,
      },
      ...(!isWixMiniApp()
        ? [
            {
              title: labelsTranslations.ipAddressTracking,
              subTitle: checkMarkIcon,
              titleClassName: styles[variant].titleClassName,
            },
          ]
        : []),
      {
        title: labelsTranslations.individualPageHistory,
        subTitle: checkMarkIcon,
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.returningVisitorTracking,
        subTitle: checkMarkIcon,
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.screenResolutionTracking,
        subTitle: checkMarkIcon,
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.visitorLocationTracking,
        subTitle: checkMarkIcon,
        titleClassName: styles[variant].titleClassName,
      },
    ];
  }, [labelsTranslations, translate, checkMarkIcon, variant]);

  const basicPrivacy = useMemo<PrivacyLevelFeature[]>(() => {
    return [
      {
        title: labelsTranslations.usesCookies,
        subTitle: translate('label.noCookieUse'),
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.regulationName,
        subTitle: translate('all.privacyCenter.privacyLevels.label.euGDPR'),
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.privacyPolicyDisclaimer,
        titleTooltip: <DisclaimerTooltip />,
        subTitle: <DisclaimerText className='mt-3' />,
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.identifyingTechnology,
        subTitle: translate('all.privacyCenter.privacyLevels.label.fingerprint'),
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.consentNeededForVA,
        subTitle: translate('all.privacyCenter.privacyLevels.label.consentDependingOnLocation'),
        titleClassName: styles[variant].titleClassName,
      },
      ...(!isWixMiniApp()
        ? [
            {
              title: labelsTranslations.ipAddressTracking,
              subTitle: closeIconDevilGray,
              titleClassName: styles[variant].titleClassName,
            },
          ]
        : []),
      {
        title: labelsTranslations.individualPageHistory,
        subTitle: checkMarkIcon,
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.returningVisitorTracking,
        subTitle: checkMarkIcon,
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.screenResolutionTracking,
        subTitle: checkMarkIcon,
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.visitorLocationTracking,
        subTitle: checkMarkIcon,
        titleClassName: styles[variant].titleClassName,
      },
    ];
  }, [labelsTranslations, translate, checkMarkIcon, closeIconDevilGray, variant]);

  const gdprMode = useMemo<PrivacyLevelFeature[]>(() => {
    return [
      {
        title: labelsTranslations.usesCookies,
        subTitle: translate('label.noCookieUse'),
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.regulationName,
        subTitle: translate('all.privacyCenter.privacyLevels.label.euEprivacy'),
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.privacyPolicyDisclaimer,
        titleTooltip: <DisclaimerTooltip />,
        subTitle: <DisclaimerText className='mt-3' />,
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.identifyingTechnology,
        subTitle: translate('all.privacyCenter.privacyLevels.label.fingerprint'),
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.consentNeededForVA,
        subTitle: translate('all.privacyCenter.privacyLevels.label.consentDependingOnLocation'),
        titleClassName: styles[variant].titleClassName,
      },
      ...(!isWixMiniApp()
        ? [
            {
              title: labelsTranslations.ipAddressTracking,
              subTitle: closeIconDevilGray,
              titleClassName: styles[variant].titleClassName,
            },
          ]
        : []),
      {
        title: labelsTranslations.individualPageHistory,
        subTitle: closeIconDevilGray,
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.returningVisitorTracking,
        subTitle: checkMarkIcon,
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.screenResolutionTracking,
        subTitle: checkMarkIcon,
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.visitorLocationTracking,
        subTitle: checkMarkIcon,
        titleClassName: styles[variant].titleClassName,
      },
    ];
  }, [labelsTranslations, translate, closeIconDevilGray, checkMarkIcon, variant]);

  const completePrivacy = useMemo<PrivacyLevelFeature[]>(() => {
    return [
      {
        title: labelsTranslations.usesCookies,
        subTitle: translate('label.noCookieUse'),
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.regulationName,
        subTitle: translate('all.privacyCenter.privacyLevels.label.germanyAndEU'),
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.privacyPolicyDisclaimer,
        titleTooltip: <DisclaimerTooltip />,
        subTitle: <DisclaimerText className='mt-3' />,
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.identifyingTechnology,
        subTitle: translate('all.privacyCenter.privacyLevels.label.noIdentifying'),
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.consentNeededForVA,
        subTitle: closeIconDevilGray,
        titleClassName: styles[variant].titleClassName,
      },
      ...(!isWixMiniApp()
        ? [
            {
              title: labelsTranslations.ipAddressTracking,
              subTitle: closeIconDevilGray,
              titleClassName: styles[variant].titleClassName,
            },
          ]
        : []),
      {
        title: labelsTranslations.individualPageHistory,
        subTitle: closeIconDevilGray,
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.returningVisitorTracking,
        subTitle: translate('all.privacyCenter.privacyLevels.label.approximate'),
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.screenResolutionTracking,
        subTitle: translate('all.privacyCenter.privacyLevels.label.approximate'),
        titleClassName: styles[variant].titleClassName,
      },
      {
        title: labelsTranslations.visitorLocationTracking,
        subTitle: checkMarkIcon,
        titleClassName: styles[variant].titleClassName,
      },
    ];
  }, [labelsTranslations, translate, checkMarkIcon, closeIconDevilGray, variant]);

  return {
    [PrivacyLevelsEnum.defaultPrivacy]: defaultPrivacy,
    [PrivacyLevelsEnum.basicPrivacy]: basicPrivacy,
    [PrivacyLevelsEnum.GDPRMode]: gdprMode,
    [PrivacyLevelsEnum.completePrivacy]: completePrivacy,
  };
};

export default usePrivacyLevelFeatures;

const DisclaimerText = ({ className }: { className: string }) => {
  const translate = useTranslate();
  const providerName = useSelector(getProviderName);
  const { onClickCopy: copyPlaceholderText, success: copyPlaceHolderTextSuccess } = useCopyToClipboard();

  const disclaimerTextForPrivacyPolicy = useMemo(
    () => translate('all.privacyCenter.privacyLevels.label.disclaimerPlaceholderText', { providerName }),
    [translate, providerName],
  );
  return (
    <div
      className={classNames(
        'flex relative border-2 border-solid border-gray-50 rounded-12 bg-white-snow w-full items-center justify-between p-2',
        className,
      )}
    >
      <TooltipWrapper trigger='click' content={disclaimerTextForPrivacyPolicy}>
        <Paragraph size={paragraphSizes.tiny} weight={fontWeights.medium} className='mr-2 line-clamp-3'>
          {disclaimerTextForPrivacyPolicy}
        </Paragraph>
      </TooltipWrapper>
      <Button
        icon={() => (copyPlaceHolderTextSuccess ? <CheckmarkIcon color='white' /> : <CopyIcon color='#3C3C3C' />)}
        onClick={() => copyPlaceholderText(disclaimerTextForPrivacyPolicy)}
        tooltip={
          copyPlaceHolderTextSuccess ? translate('button.copiedToClipboard') : translate('button.copyToClipboardText')
        }
        color={'quaternary'}
        className='h-42px w-42px'
        data-testid={TEST_IDS.generic.buttons.copyToClipboard}
      />
    </div>
  );
};

const DisclaimerTooltip = () => {
  const translate = useTranslate();
  const supportCenterDomain = useSelector(getSupportCenterDomain);

  return (
    <div className='p-5 rounded-12 flex flex-col text-left'>
      <Paragraph className='text-21px leading-30' weight={fontWeights.semibold}>
        {translate('all.privacyCenter.privacyLevels.label.privacyPolicyDisclaimerTooltipTitle')}
      </Paragraph>
      <Paragraph className='mt-1.5 mb-6' weight={fontWeights.normal}>
        {translate('all.privacyCenter.privacyLevels.label.privacyPolicyDisclaimerTooltipDescription')}
      </Paragraph>
      <PageHeaderTooltipOption
        text={translate('tooltip_supportCenterText.default.label')}
        icon={() => <CustomerSupport color={'#3C3C3C'} className='shrink-0' />}
        url={translate('tooltipTab.privacyCenter_supportCenterUrl', { supportCenterDomain })}
      />
    </div>
  );
};
